import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRegions } from '../api';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 20;

/**
 * Multi-select фильтр по регионам компаний с поиском (debounce) и infinite scroll.
 * selected: number[] — выбранные коды регионов
 * onChange: (codes: number[]) => void
 */
const RegionsFilter = ({ selected = [], onChange, translationKey = 'users' }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  // Запоминаем названия выбранных регионов, чтобы не терять их при поиске/пагинации.
  const [selectedLabels, setSelectedLabels] = useState({});

  const requestIdRef = useRef(0);

  // Debounce поискового ввода
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Загрузка первой страницы при изменении поискового запроса
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);

    getRegions(debouncedSearch, 0, PAGE_SIZE)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setOptions(data.content || []);
        setPage(0);
        setTotalPages(data.totalPages || 0);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error('Ошибка загрузки регионов:', err);
        setOptions([]);
        setTotalPages(0);
        setError(true);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [debouncedSearch]);

  // Подгрузка следующей страницы (infinite scroll)
  const loadMore = () => {
    if (loading || loadingMore) return;
    if (page + 1 >= totalPages) return;

    const requestId = requestIdRef.current;
    const nextPage = page + 1;
    setLoadingMore(true);

    getRegions(debouncedSearch, nextPage, PAGE_SIZE)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setOptions((prev) => [...prev, ...(data.content || [])]);
        setPage(nextPage);
        setTotalPages(data.totalPages || 0);
      })
      .catch((err) => {
        console.error('Ошибка загрузки регионов:', err);
      })
      .finally(() => {
        setLoadingMore(false);
      });
  };

  const handleListScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      loadMore();
    }
  };

  // Запоминаем подписи выбранных регионов по мере появления опций
  useEffect(() => {
    if (options.length === 0) return;
    setSelectedLabels((prev) => {
      let changed = false;
      const next = { ...prev };
      options.forEach((opt) => {
        if (selected.includes(opt.code) && next[opt.code] !== opt.name) {
          next[opt.code] = opt.name;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [options, selected]);

  const toggleRegion = (code) => {
    if (selected.includes(code)) {
      onChange(selected.filter((c) => c !== code));
    } else {
      onChange([...selected, code]);
    }
  };

  const removeRegion = (code) => {
    onChange(selected.filter((c) => c !== code));
  };

  const clearAll = () => {
    onChange([]);
    setSelectedLabels({});
  };

  const getLabel = (code) => {
    const fromOptions = options.find((o) => o.code === code);
    if (fromOptions) return fromOptions.name;
    return selectedLabels[code] ?? code;
  };

  const hasSelection = selected.length > 0;

  return (
    <div className="more-filters-item more-filters-categories-block">
      <div className="more-filters-categories-header">
        <label className="more-filters-label">
          {t(`${translationKey}.filters.regions`)}
        </label>
        {hasSelection && (
          <button type="button" className="more-filters-categories-clear" onClick={clearAll}>
            {t(`${translationKey}.filters.regionsClearSelected`)}
          </button>
        )}
      </div>
      <p className="more-filters-categories-hint">
        {t(`${translationKey}.filters.regionsHint`)}
      </p>

      <input
        type="search"
        className="sort-menu-text-input more-filters-categories-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t(`${translationKey}.filters.regionsSearchPlaceholder`)}
        style={{
          border: '2px solid var(--accent-primary)',
          borderRadius: '8px',
          padding: '8px 12px',
          width: '100%',
          background: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          fontSize: '0.9rem',
        }}
      />

      {hasSelection && (
        <div className="more-filters-category-chips" aria-live="polite">
          {selected.map((code) => (
            <button
              key={code}
              type="button"
              className="more-filters-category-chip"
              onClick={() => removeRegion(code)}
              title={t(`${translationKey}.filters.regionsRemoveChip`)}
            >
              <span className="more-filters-category-chip-label">{getLabel(code)}</span>
              <span className="more-filters-category-chip-x" aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}

      {error ? (
        <div className="more-filters-categories-status more-filters-categories-error">
          {t(`${translationKey}.filters.regionsError`)}
        </div>
      ) : loading ? (
        <div className="more-filters-categories-status">
          {t(`${translationKey}.filters.regionsLoading`)}
        </div>
      ) : (
        <div className="more-filters-categories-list" onScroll={handleListScroll}>
          {options.length === 0 ? (
            <div className="more-filters-categories-status">
              {t(`${translationKey}.filters.regionsEmpty`)}
            </div>
          ) : (
            <>
              {options.map((opt) => (
                <label key={opt.code} className="more-filters-category-row">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.code)}
                    onChange={() => toggleRegion(opt.code)}
                  />
                  <span className="more-filters-category-title">{opt.name}</span>
                </label>
              ))}
              {loadingMore && (
                <div className="more-filters-categories-status">
                  {t(`${translationKey}.filters.regionsLoading`)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RegionsFilter;
